import React, { useState } from 'react';
import { Table, Button, Space, Tag, Modal, message, Popconfirm, Switch } from 'antd';
import { EditOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';
import partnerUsersApi from '../../api/partnerUsersApi';
import PartnerUserForm from './PartnerUserForm';
import ResetPasswordModal from './ResetPasswordModal';

const PartnerUserTable = ({ partnerId, users, loading, onRefresh }) => {
  const [editingUser, setEditingUser] = useState(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [isResetPasswordVisible, setIsResetPasswordVisible] = useState(false);
  const [formLoading, setFormLoading] = useState(false);

  const handleEdit = (user) => {
    setEditingUser(user);
    setIsFormVisible(true);
  };

  const handleDelete = async (userId) => {
    try {
      await partnerUsersApi.delete(partnerId, userId);
      message.success('User deleted successfully');
      onRefresh();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete user');
    }
  };

  const handleFormClose = () => {
    setEditingUser(null);
    setIsFormVisible(false);
  };

  const handleFormSubmit = async (formData) => {
    setFormLoading(true);
    try {
      if (editingUser) {
        await partnerUsersApi.update(partnerId, editingUser.id, formData);
        message.success('User updated successfully');
      } else {
        await partnerUsersApi.create(partnerId, formData);
        message.success('User created successfully');
      }
      handleFormClose();
      onRefresh();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await partnerUsersApi.toggleActive(partnerId, user.id);
      message.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      onRefresh();
    } catch (error) {
      message.error('Failed to toggle user status');
    }
  };

  const handleResetPassword = (user) => {
    setResetPasswordUser(user);
    setIsResetPasswordVisible(true);
  };

  const handleResetPasswordClose = () => {
    setResetPasswordUser(null);
    setIsResetPasswordVisible(false);
  };

  const handleResetPasswordSubmit = async (newPassword) => {
    try {
      await partnerUsersApi.resetPassword(partnerId, resetPasswordUser.id, newPassword);
      message.success('Password reset successfully');
      handleResetPasswordClose();
      onRefresh();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role) => {
        const colors = { admin: 'red', manager: 'blue', user: 'green' };
        return <Tag color={colors[role] || 'default'}>{role?.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleActive(record)}
        />
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'last_login_at',
      key: 'last_login_at',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'Never',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<LockOutlined />}
            onClick={() => handleResetPassword(record)}
          >
            Reset Pass
          </Button>
          <Button
            type="default"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete User"
            description="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="default"
              danger
              size="small"
              icon={<DeleteOutlined />}
            >
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={users}
        loading={loading}
        rowKey="id"
        pagination={{ pageSize: 10 }}
      />

      <PartnerUserForm
        visible={isFormVisible}
        user={editingUser}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
      />

      <ResetPasswordModal
        visible={isResetPasswordVisible}
        user={resetPasswordUser}
        onClose={handleResetPasswordClose}
        onSubmit={handleResetPasswordSubmit}
      />
    </>
  );
};

export default PartnerUserTable;
